import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE_URL = "https://customersupport-jhy9.onrender.com/api";

function SaveData() {
  const [options, setOptions] = useState({
    services: [],
    materials: [],
    locations: [],
    cities: [],
    subjects: [],
    descriptions: [],
    customerNames: [] // Added customerNames
  });

  const [selectedField, setSelectedField] = useState("services");
  const [newValues, setNewValues] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [authError, setAuthError] = useState(false);

  const fieldLabels = {
    services: "Services",
    materials: "Materials",
    locations: "Departments",
    cities: "Cities",
    subjects: "Nature of Problems",
    descriptions: "Problem Descriptions",
    customerNames: "Customer Names" // Added customerNames
  };

  const getAuthToken = () => {
    return localStorage.getItem("authToken");
  };

  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const decodeToken = () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No token found");
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error("Invalid token format");
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        console.error("Token expired");
        return null;
      }

      let effectiveCompanyId;
      if (payload.role === 'company') {
        effectiveCompanyId = payload.id;
      } else {
        effectiveCompanyId = payload.companyId;
      }

      if (!effectiveCompanyId) {
        console.error("No company ID in token");
        return null;
      }

      return {
        companyId: effectiveCompanyId,
        role: payload.role,
        userId: payload.id,
        email: payload.email,
        name: payload.name || payload.username,
        exp: payload.exp
      };
    } catch (error) {
      console.error("Token decode error:", error);
      return null;
    }
  };

  useEffect(() => {
    const userData = decodeToken();
    if (userData) {
      setCompanyId(userData.companyId);
      setUserInfo(userData);
      loadOptions(userData.companyId);
    } else {
      setAuthError(true);
    }
  }, []);

  const loadOptions = async (cid) => {
    if (!cid) {
      console.error("No company ID provided");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ticket-options/${cid}`,
        { headers: getAuthHeaders() }
      );

      if (response.data?.success && response.data?.options) {
        // Ensure customerNames exists in the response
        const optionsData = response.data.options;
        setOptions({
          services: optionsData.services || [],
          materials: optionsData.materials || [],
          locations: optionsData.locations || [],
          cities: optionsData.cities || [],
          subjects: optionsData.subjects || [],
          descriptions: optionsData.descriptions || [],
          customerNames: optionsData.customerNames || [] // Handle customerNames
        });
      } else {
        setOptions({
          services: [],
          materials: [],
          locations: [],
          cities: [],
          subjects: [],
          descriptions: [],
          customerNames: [] // Initialize as empty array
        });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      
      if (error.response?.status === 401) {
        setAuthError(true);
        alert("Session expired. Please login again.");
      } else if (error.response?.status === 404) {
        setOptions({
          services: [],
          materials: [],
          locations: [],
          cities: [],
          subjects: [],
          descriptions: [],
          customerNames: [] // Initialize as empty array
        });
      } else {
        alert(`Error loading data: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveOptions = async () => {
    if (!companyId) {
      alert("No company ID available");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        companyId: companyId,
        category: selectedField,
        value: options[selectedField]
      };

      const response = await axios.post(
        `${API_BASE_URL}/ticket-options/add`,
        payload,
        { headers: getAuthHeaders() }
      );

      if (response.data?.success) {
        alert("Saved successfully");
        await loadOptions(companyId);
      }
    } catch (error) {
      console.error("Save error:", error);
      
      if (error.response?.status === 401) {
        setAuthError(true);
        alert("Session expired. Please login again.");
      } else {
        alert(`Save failed: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const addValues = (valuesText, multiple = false) => {
    if (!valuesText.trim()) return;

    let newVals;
    if (multiple) {
      newVals = valuesText
        .split('\n')
        .flatMap(line => line.split(','))
        .map(v => v.trim())
        .filter(v => v && !options[selectedField].includes(v));
    } else {
      const val = valuesText.trim();
      newVals = val && !options[selectedField].includes(val) ? [val] : [];
    }

    if (newVals.length > 0) {
      setOptions(prev => ({
        ...prev,
        [selectedField]: [...prev[selectedField], ...newVals]
      }));
      setNewValues("");
    }
  };

  const removeValue = async (index) => {
    if (!companyId) return;

    const itemToDelete = options[selectedField][index];
    if (!itemToDelete) return;

    // Store original data for potential rollback
    const originalOptions = [...options[selectedField]];
    
    // Local delete first (UI update)
    setOptions(prev => ({
      ...prev,
      [selectedField]: prev[selectedField].filter((_, i) => i !== index)
    }));
    setSelectedItems(prev => prev.filter(i => i !== index));

    try {
      // Use DELETE endpoint
      const response = await axios.delete(
        `${API_BASE_URL}/ticket-options/${companyId}/${selectedField}/${index}`,
        { headers: getAuthHeaders() }
      );

      if (response.data?.success) {
        console.log('Item deleted successfully');
      } else {
        throw new Error('Delete failed on server');
      }
    } catch (error) {
      console.error("Delete error:", error);
      
      // Revert local changes if backend fails
      setOptions(prev => ({
        ...prev,
        [selectedField]: originalOptions
      }));
      
      if (error.response?.status === 401) {
        setAuthError(true);
        alert("Session expired. Please login again.");
      } else {
        alert(`Delete failed: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const removeSelected = async () => {
    if (!companyId || selectedItems.length === 0) return;

    // Store original data
    const originalOptions = [...options[selectedField]];
    
    // Local delete first
    setOptions(prev => ({
      ...prev,
      [selectedField]: prev[selectedField].filter((_, i) => !selectedItems.includes(i))
    }));

    const itemsToDelete = selectedItems;
    setSelectedItems([]);

    try {
      // For bulk delete, send the updated array
      const response = await axios.put(
        `${API_BASE_URL}/ticket-options/${companyId}`,
        {
          companyId: companyId,
          category: selectedField,
          value: options[selectedField].filter((_, i) => !itemsToDelete.includes(i))
        },
        { headers: getAuthHeaders() }
      );

      if (response.data?.success) {
        console.log('Bulk delete successful');
      } else {
        throw new Error('Bulk delete failed');
      }
    } catch (error) {
      console.error("Bulk delete error:", error);
      
      // Revert on error
      setOptions(prev => ({
        ...prev,
        [selectedField]: originalOptions
      }));
      
      alert(`Bulk delete failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const startEdit = (index, value) => {
    setEditingIndex(index);
    setEditValue(value);
  };

  const saveEdit = () => {
    if (editingIndex !== null && editValue.trim()) {
      setOptions(prev => ({
        ...prev,
        [selectedField]: prev[selectedField].map((item, i) => 
          i === editingIndex ? editValue.trim() : item
        )
      }));
      setEditingIndex(null);
      setEditValue("");
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  const toggleSelect = (index) => {
    setSelectedItems(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const selectAll = () => {
    const filtered = getFilteredOptions();
    const indices = options[selectedField]
      .map((item, idx) => filtered.includes(item) ? idx : null)
      .filter(idx => idx !== null);
    setSelectedItems(indices);
  };

  const clearSelection = () => setSelectedItems([]);

  const importValues = () => {
    const values = importData
      .split('\n')
      .flatMap(line => line.split(','))
      .map(v => v.trim())
      .filter(v => v && !options[selectedField].includes(v));

    if (values.length > 0) {
      setOptions(prev => ({
        ...prev,
        [selectedField]: [...prev[selectedField], ...values]
      }));
      setShowImportModal(false);
      setImportData("");
    }
  };

  const exportValues = () => {
    const data = options[selectedField].join('\n');
    navigator.clipboard.writeText(data);
    alert("Copied to clipboard");
  };

  const getFilteredOptions = () => {
    return options[selectedField].filter(item =>
      item.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredOptions = getFilteredOptions();

  if (authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-4">Your session has expired or you are not logged in.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Ticket Options Manager
          </h1>
          <p className="text-gray-600">Manage dropdown options for support tickets</p>
          {userInfo && (
            <div className="mt-4 inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-lg text-sm">
              <span className="font-semibold">Company:</span> {companyId} | 
              <span className="font-semibold ml-2">User:</span> {userInfo.name || userInfo.email}
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Category</h3>
              <select
                value={selectedField}
                onChange={(e) => {
                  setSelectedField(e.target.value);
                  setSelectedItems([]);
                  setSearchTerm("");
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.keys(fieldLabels).map(key => (
                  <option key={key} value={key}>{fieldLabels[key]}</option>
                ))}
              </select>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Add Options</h3>
              
              <textarea
                value={newValues}
                onChange={(e) => setNewValues(e.target.value)}
                placeholder="Enter values (comma or line separated)"
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              />
              
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => addValues(newValues, false)}
                  disabled={!newValues.trim()}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Add Single
                </button>
                <button
                  onClick={() => addValues(newValues, true)}
                  disabled={!newValues.trim()}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                >
                  Add Multiple
                </button>
              </div>

              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700"
                >
                  Import
                </button>
                <button
                  onClick={exportValues}
                  className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700"
                >
                  Export
                </button>
              </div>
            </div>

            {selectedItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <h4 className="font-semibold text-red-800 mb-2">
                  Bulk Actions ({selectedItems.length})
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={removeSelected}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={clearSelection}
                    className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700"
                  >
                    Clear
                  </button>
                </div>
              </motion.div>
            )}

            <button
              onClick={saveOptions}
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-lg hover:from-green-700 hover:to-blue-700 disabled:opacity-50 shadow-lg"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>

            <button
              onClick={() => loadOptions(companyId)}
              disabled={loading}
              className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">
                  {fieldLabels[selectedField]} ({options[selectedField].length})
                </h3>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={selectAll}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Select All
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                <AnimatePresence>
                  {filteredOptions.map((item, index) => {
                    const originalIndex = options[selectedField].indexOf(item);
                    return (
                      <motion.div
                        key={originalIndex}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={`flex items-center justify-between p-3 border-b hover:bg-gray-50 ${
                          selectedItems.includes(originalIndex) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(originalIndex)}
                            onChange={() => toggleSelect(originalIndex)}
                            className="w-4 h-4"
                          />
                          
                          {editingIndex === originalIndex ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 p-2 border border-blue-300 rounded"
                              autoFocus
                            />
                          ) : (
                            <span className="flex-1">{item}</span>
                          )}
                        </div>

                        <div className="flex gap-2">
                          {editingIndex === originalIndex ? (
                            <>
                              <button
                                onClick={saveEdit}
                                className="text-green-600 hover:text-green-800 px-2"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="text-red-600 hover:text-red-800 px-2"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => startEdit(originalIndex, item)}
                                className="text-blue-600 hover:text-blue-800 px-2"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => removeValue(originalIndex)}
                                className="text-red-600 hover:text-red-800 px-2"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {filteredOptions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm ? "No matches" : "No options yet"}
                  </div>
                )}
              </div>

              <div className="mt-4 text-sm text-gray-600">
                Showing {filteredOptions.length} of {options[selectedField].length}
                {searchTerm && ` (filtered)`}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowImportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-semibold mb-4">Import Options</h3>
              
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="Paste values (comma or line separated)"
                rows={8}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              />
              
              <div className="flex gap-2 mt-4">
                <button
                  onClick={importValues}
                  disabled={!importData.trim()}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Import
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportData("");
                  }}
                  className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SaveData;